// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import Skeleton from "react-loading-skeleton"
import { useQuery } from "@tanstack/react-query"
import { Rating } from "react-simple-star-rating"

// ** UI Component
import { Button } from "../../button"
import { Avatar, AvatarImage } from "../../avatar"

// ** Routes
import { SERVER_URL, WORK_DIARY_GET_NOTE_BY_ID } from "@pma/utils/api-routes"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Types
import { DictType } from "@pma/types"

// ** Helpers
import { cn, parseMessage } from "@pma/utils/helpers"

// ** Constants
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

interface PreviewDiaryProps {
  workDiaries: DictType | undefined
  project: DictType | undefined
  setTaskModal: React.Dispatch<React.SetStateAction<boolean>>
  setActiveStep: React.Dispatch<React.SetStateAction<number>>
}

const PreviewDiary = ({ workDiaries, project, setTaskModal, setActiveStep }: PreviewDiaryProps) => {
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const isHebrew = locale === "he"
  const organizationUsers = authConfig?.organizationUsers || []

  const resourcesList = workDiaries?._diary_resourses?.filter(
    (obj: any) => obj._resource?.structures,
  )
  const workFlowList = workDiaries?._diary_resourses?.filter((obj: any) => obj._resource?.employees)
  const toolsList = workDiaries?._diary_resourses?.filter((obj: any) => obj._resource?.tools)

  const RenderAssignTo = ({ user }: any) => {
    return (
      <Avatar className='mr-1 h-5 w-5'>
        <AvatarImage
          src={user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`}
        />
      </Avatar>
    )
  }

  const fetchComments = async () => {
    if (!workDiaries?.id) return []

    const response = await axios.get(
      SERVER_URL + WORK_DIARY_GET_NOTE_BY_ID + `/${workDiaries?.id}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const { data: comments, isLoading } = useQuery({
    queryKey: [`work-diary-comments-${workDiaries?.id}`, workDiaries?.id],
    queryFn: fetchComments,
    enabled: !!workDiaries?.id,
  })

  return (
    <>
      <div
        className={cn(
          "mx-auto max-w-4xl rounded-lg border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-900",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        <h2 className='mb-1 text-center text-2xl font-bold text-gray-900 dark:text-gray-100'>
          {translate("Work Logs")}
        </h2>
        <p className='mb-6 text-center text-sm text-gray-500 dark:text-gray-400'>
          {new Date(workDiaries?.created_at).toLocaleString()}
        </p>

        <div className='grid grid-cols-2 gap-4 text-sm'>
          <div>
            <p className='font-semibold text-gray-800 dark:text-gray-200'>
              {translate("Project Name")}
            </p>
            <p className='text-gray-600 dark:text-gray-400'>{project?.title}</p>
          </div>
          <div>
            <p className='font-semibold text-gray-800 dark:text-gray-200'>
              {translate("Contractor Company Name")}
            </p>
            <p className='text-gray-600 dark:text-gray-400'>{workDiaries?._company?.name}</p>
          </div>
          <div>
            <p className='font-semibold text-gray-800 dark:text-gray-200'>{translate("Status")}</p>
            <p className='text-gray-600 dark:text-gray-400'>{isHebrew ? workDiaries?._status?.he : workDiaries?._status?.en}</p>
          </div>
          <div>
            <p className='font-semibold text-gray-800 dark:text-gray-200'>
              {translate("Site Address")}
            </p>
            <p className='text-gray-600 dark:text-gray-400'>{workDiaries?.address}</p>
          </div>
          <div>
            <p className='font-semibold text-gray-800 dark:text-gray-200'>
              {translate("Description and Content")}
            </p>
            <p className='text-gray-600 dark:text-gray-400'>{workDiaries?.description}</p>
          </div>
        </div>

        <div className='mt-6'>
          <p className='font-semibold text-gray-800 dark:text-gray-200'>
            {translate("Weather Conditions")}
          </p>
          <p className='text-gray-600 dark:text-gray-400'>
            {locale == "he" ? workDiaries?._weather?.he : workDiaries?._weather?.en}{" "}
            {`(${workDiaries?.temperature}°C)`}{" "}
            <Rating
              size={16}
              initialValue={workDiaries?.weather_rating}
              readonly
              SVGstyle={{ display: "inline-block" }}
            />
          </p>
        </div>

        <div className='mt-6'>
          <p className='font-semibold text-gray-800 dark:text-gray-200'>
            {translate("Work Details")}
          </p>
          <ul className='list-inside list-disc text-gray-600 dark:text-gray-400'>
            {workDiaries?._work_details?.map((detail: any, index: number) => (
              <li key={index}>
                {detail?.description} - {`${detail?.amount} ${detail?.units}`}
              </li>
            ))}
          </ul>
        </div>

        <div className='mt-6'>
          <p className='font-semibold text-gray-800 dark:text-gray-200'>{translate("Resources")}</p>
          <ul className='list-inside list-disc text-gray-600 dark:text-gray-400'>
            {resourcesList?.length ? (
              resourcesList.map((details: any, index: number) => (
                <li key={`e_${index}`}>
                  {locale == "he" ? details?._resource?.he : details?._resource?.en}:{" "}
                  {details?.amount} {`(${details?.remarks})`}
                </li>
              ))
            ) : (
              <>{translate("No Record Found")}</>
            )}
          </ul>
        </div>

        <div className='mt-6'>
          <p className='font-semibold text-gray-800 dark:text-gray-200'>{translate("Workforce")}</p>
          <ul className='list-inside list-disc text-gray-600 dark:text-gray-400'>
            {workFlowList?.length ? (
              workFlowList.map((details: any, index: number) => (
                <li key={`e_${index}`}>
                  {locale == "he" ? details?._resource?.he : details?._resource?.en}:{" "}
                  {details?.amount} {`(${details?.remarks})`}
                </li>
              ))
            ) : (
              <>{translate("No Record Found")}</>
            )}
          </ul>
        </div>

        <div className='mt-6'>
          <p className='font-semibold text-gray-800 dark:text-gray-200'>
            {`${translate("Equipment")} & ${translate("Tools")}`}
          </p>
          <ul className='list-inside list-disc text-gray-600 dark:text-gray-400'>
            {toolsList?.length ? (
              toolsList.map((details: any, index: number) => (
                <li key={`r_${index}`}>
                  {locale == "he" ? details?._resource?.he : details?._resource?.en}:{" "}
                  {details?.amount} {`(${details?.remarks})`}
                </li>
              ))
            ) : (
              <>{translate("No Record Found")}</>
            )}
          </ul>
        </div>

        <div className='mt-6'>
          <p className='font-semibold text-gray-800 dark:text-gray-200'>{translate("Tasks")}</p>
          <ul className='list-inside list-disc text-gray-600 dark:text-gray-400'>
            {workDiaries?._tasks?.map((task: any, index: number) => (
              <li
                className={`flex items-center ${task?.finish_to == 1 && "line-through"}`}
                key={`t_${index}`}
              >
                {task?.description} -
                <p className='ml-2 flex'>
                  {organizationUsers.map((user: any) => {
                    if (task.responsible_id.includes(user.id)) {
                      return <RenderAssignTo key={user.id} user={user} />
                    } else return null
                  })}
                </p>
              </li>
            ))}
          </ul>
        </div>

        <div className='mt-6'>
          <h3 className='mb-3 border-b pb-2 text-lg font-bold text-gray-800 dark:text-gray-200'>
            {translate("Comments")}
          </h3>

          {isLoading ? (
            <div className='space-y-3'>
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className='flex gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800'
                >
                  <Skeleton circle width={32} height={32} />
                  <div className='flex-1'>
                    <div className='flex items-center gap-2'>
                      <Skeleton width={100} height={16} />
                      <Skeleton width={60} height={12} />
                    </div>
                    <Skeleton count={2} height={16} className='mt-2' />
                  </div>
                </div>
              ))}
            </div>
          ) : comments?.length > 0 ? (
            <div className='space-y-3'>
              {comments.map((comment: any) => (
                <div
                  key={comment.id}
                  className='flex gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800'
                >
                  <Avatar className='h-8 w-8 shrink-0'>
                    <AvatarImage
                      src={
                        comment.user?.user_picture?.url ||
                        `${process.env.PUBLIC_URL}/user-profile.svg`
                      }
                    />
                  </Avatar>
                  <div className='flex-1'>
                    <div className='flex items-center gap-2'>
                      <span className='font-medium text-gray-800 dark:text-gray-200'>
                        {comment.user?.name}
                      </span>
                      <span className='text-xs text-gray-500 dark:text-gray-400'>
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: parseMessage(comment?.note, organizationUsers),
                      }}
                      className='mt-1 text-gray-600 dark:text-gray-400'
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className='text-gray-500 dark:text-gray-400'>{translate("No Record Found")}</p>
          )}
        </div>
      </div>

      <div className='flex flex-col justify-between gap-4 border-t pt-6 sm:flex-row'>
        <Button
          type='button'
          variant='outline'
          className='w-full sm:w-auto'
          onClick={() => setActiveStep(3)}
        >
          {translate("Previous")}
        </Button>
        <Button className='w-full sm:w-auto' onClick={() => setTaskModal(false)}>
          {translate("Complete")}
        </Button>
      </div>
    </>
  )
}

export default PreviewDiary
